import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react'
import { get } from 'lodash'
import classnames from 'classnames'
import { useSelector } from 'react-redux'
import { State } from '../../../reducers'
import { UserStore } from '../../../typings/stores/UserStore'
import {
  DateRangeFilter,
  FilterBox,
  Select,
  GenericInput,
} from '../../../components'
import { convertPeriodToDates } from '../../../utils/dates'

import styles from './filters.module.css'
import { useProductFamiliesLazyQuery } from '../../../generated/types'
import { decode } from 'he'

interface TableFiltersProps {
  tableContext: any
  showDateType?: boolean
  showEvent?: boolean
  showClickedProducts?: boolean
  showSubTracking?: boolean
  setPropertyHasData?: any
}

const TableFilters: FunctionComponent<TableFiltersProps> = ({
  tableContext,
  showDateType = false,
  showEvent = false,
  showClickedProducts = false,
  showSubTracking = false,
  setPropertyHasData = null,
}) => {
  const { setFilter, setFilters, filters } = useContext(tableContext)
  const [verticals, setVerticals] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [programs, setPrograms] = useState([])
  const [products, setProducts] = useState([])
  const [campaigns, setCampaigns] = useState([])
  const [events, setEvents] = useState([])
  const [clickedProduct, setClickedProduct] = useState([])
  const defaultPeriod = 'this_month'
  const defaultStartDate = convertPeriodToDates(defaultPeriod).startDate
  const defaultEndDate = convertPeriodToDates(defaultPeriod).endDate
  const allEvents = [
    {
      value: 'sale',
      text: 'Sale',
    },
    {
      value: 'app',
      text: 'Application',
    },
    {
      value: 'dec',
      text: 'Decline',
    },
  ]

  const user = useSelector<State, UserStore>(state => state.user)

  const [
    ProductFamiliesResult,
    { data, loading },
  ] = useProductFamiliesLazyQuery()

  const fetchData = () => {
    ProductFamiliesResult({
      variables: {
        take: 3000,
        externalPropertyId: user.activeWebsite.id.toString(),
      },
    })
  }

  useEffect(() => {
    if (!filters.startDate || !filters.endDate) {
      filters.startDate = defaultStartDate
      filters.endDate = defaultEndDate
      filters.dateType = 'clickDate'
      filters.event = 'sale'
      if (showClickedProducts) {
        filters.groupBy = 'byDate'
        filters.vertical = '5'
      }
    }
  }, [])

  useEffect(() => {
    if (filters?.vertical === '5') {
      setEvents(allEvents)
    } else {
      setEvents([allEvents[0]])
      setFilter('event', 'sale')
    }
  }, [filters?.vertical])

  useEffect(fetchData, [user.activeWebsite])

  useEffect(() => {
    if (data && data.productFamilies) {
      const verticalsSet = []
      const productTypesSet = []
      const programsSet = []
      const productsSet = []
      const campaignsSet = []
      const clickedProductSet = []
      for (const propertyProduct of data.productFamilies.page) {
        if (
          !verticalsSet.find(
            x => x.id === propertyProduct.externalVerticalId
          ) &&
          propertyProduct.verticalName
        ) {
          verticalsSet.push({
            id: propertyProduct.externalVerticalId,
            name: propertyProduct.verticalName,
          })
        }
        if (
          !productTypesSet.find(
            x => x.id === propertyProduct.externalProductTypeId
          ) &&
          propertyProduct.productTypeName
        ) {
          productTypesSet.push({
            id: propertyProduct.externalProductTypeId,
            name: propertyProduct.productTypeName,
            verticalId: propertyProduct.externalVerticalId,
          })
        }
        if (
          !programsSet.find(x => x.id === propertyProduct.externalProgramId) &&
          propertyProduct.programName
        ) {
          programsSet.push({
            id: propertyProduct.externalProgramId,
            name: propertyProduct.programName,
            verticalId: propertyProduct.externalVerticalId,
            productTypeId: propertyProduct.externalProductTypeId,
          })
        }
        if (
          !productsSet.find(
            x => x.id === propertyProduct.externalDisplayedProductId
          ) &&
          propertyProduct.displayedProductName
        ) {
          productsSet.push({
            id: propertyProduct.externalDisplayedProductId,
            name: propertyProduct.displayedProductName,
            verticalId: propertyProduct.externalVerticalId,
            productTypeId: propertyProduct.externalProductTypeId,
            programId: propertyProduct.externalProgramId,
          })
        }
        if (
          !campaignsSet.find(
            x => x.id === propertyProduct.externalCampaignId
          ) &&
          propertyProduct.campaignName
        ) {
          campaignsSet.push({
            id: propertyProduct.externalCampaignId,
            name: propertyProduct.campaignName,
            verticalId: propertyProduct.externalVerticalId,
            productTypeId: propertyProduct.externalProductTypeId,
            programId: propertyProduct.externalProgramId,
            productId: propertyProduct.externalClickedProductId,
          })
        }
        if (
          !clickedProductSet.find(
            x => x.id === propertyProduct.externalClickedProductId
          ) &&
          propertyProduct.clickedProductName
        ) {
          clickedProductSet.push({
            id: propertyProduct.externalClickedProductId,
            name: propertyProduct.clickedProductName,
            verticalId: propertyProduct.externalVerticalId,
          })
        }
      }
      verticalsSet.sort((a, b) => a.name.localeCompare(b.name))
      productTypesSet.sort((a, b) => a.name.localeCompare(b.name))
      programsSet.sort((a, b) => a.name.localeCompare(b.name))
      productsSet.sort((a, b) => a.name.localeCompare(b.name))
      campaignsSet.sort((a, b) => a.name.localeCompare(b.name))
      clickedProductSet.sort((a, b) => a.name.localeCompare(b.name))
      setVerticals(verticalsSet)
      setProductTypes(productTypesSet)
      setPrograms(programsSet)
      setProducts(productsSet)
      setCampaigns(campaignsSet)
      setClickedProduct(clickedProductSet)
    }

    if (setPropertyHasData) {
      if (data?.productFamilies?.page?.length === 0) {
        setPropertyHasData(false)
      } else if (data?.productFamilies?.page?.length > 0) {
        setPropertyHasData(true)
      }
    }
  }, [data])

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        {showDateType && (
          <FilterBox label="Date type">
            <div className={styles.dateTypeContainer}>
              <button
                className={classnames(styles.toggleBtn, {
                  [styles.selected]: filters.dateType === 'clickDate',
                })}
                onClick={() => setFilter('dateType', 'clickDate')}
              >
                Click Date
              </button>
              <button
                className={classnames(styles.toggleBtn, {
                  [styles.selected]: filters.dateType === 'processDate',
                })}
                onClick={() => setFilter('dateType', 'processDate')}
              >
                Process Date
              </button>
            </div>
          </FilterBox>
        )}
        <DateRangeFilter
          onChange={setFilters}
          dates={{
            startDate: filters.startDate ?? defaultEndDate,
            endDate: filters.endDate ?? defaultStartDate,
            dateRange: filters.dateRange ?? defaultPeriod,
          }}
        />
      </div>
      <div className={styles.row}>
        <FilterBox label="Filter by" className={styles.filterBy}>
          <div className={styles.filterByContainer}>
            <Select
              className={styles.dropdown}
              placeholder={showClickedProducts ? null : 'All Verticals'}
              loadingMessage="Loading verticals"
              isLoading={loading}
              selected={get(filters, 'vertical', '')}
              options={verticals.map(pt => ({
                value: pt.id,
                text: decode(pt.name),
              }))}
              onChange={e => {
                setFilter('vertical', e.currentTarget.value)
              }}
            />
            {showEvent && (
              <Select
                className={styles.dropdown}
                selected={get(filters, 'event', 'sale')}
                options={events}
                onChange={e => setFilter('event', e.currentTarget.value)}
              />
            )}
            <Select
              className={styles.dropdown}
              placeholder="All Product Types"
              loadingMessage="Loading product types"
              isLoading={loading}
              selected={get(filters, 'productType', '')}
              options={
                filters.vertical
                  ? productTypes
                      .filter(pt => pt.verticalId === filters.vertical)
                      .map(pt => ({
                        value: pt.id,
                        text: decode(pt.name),
                      }))
                  : productTypes.map(pt => ({
                      value: pt.id,
                      text: decode(pt.name),
                    }))
              }
              onChange={e => setFilter('productType', e.currentTarget.value)}
            />

            <Select
              className={styles.dropdown}
              placeholder="All Displayed Products"
              loadingMessage="Loading products"
              isLoading={loading}
              selected={get(filters, 'product', '')}
              options={
                filters.vertical
                  ? products
                      .filter(pt => pt.verticalId === filters.vertical)
                      .map(pt => ({
                        value: pt.id,
                        text: decode(pt.name),
                      }))
                  : products.map(pt => ({
                      value: pt.id,
                      text: decode(pt.name),
                    }))
              }
              onChange={e => setFilter('product', e.currentTarget.value)}
            />
            <Select
              className={styles.dropdown}
              placeholder="All Programs"
              loadingMessage="Loading programs"
              isLoading={loading}
              selected={get(filters, 'program', '')}
              options={
                filters.vertical
                  ? programs
                      .filter(pt => pt.verticalId === filters.vertical)
                      .map(pt => ({
                        value: pt.id,
                        text: decode(pt.name),
                      }))
                  : programs.map(pt => ({
                      value: pt.id,
                      text: decode(pt.name),
                    }))
              }
              onChange={e => setFilter('program', e.currentTarget.value)}
            />
            <Select
              className={styles.dropdown}
              placeholder="All Clicked Products"
              loadingMessage="Loading Clicked roducts"
              isLoading={loading}
              selected={get(filters, 'clickedProduct', '')}
              options={
                filters.vertical
                  ? clickedProduct
                      .filter(pt => pt.verticalId === filters.vertical)
                      .map(pt => ({
                        value: pt.id,
                        text: decode(pt.name),
                      }))
                  : clickedProduct.map(pt => ({
                      value: pt.id,
                      text: decode(pt.name),
                    }))
              }
              onChange={e => setFilter('clickedProduct', e.currentTarget.value)}
            />
            <Select
              className={styles.dropdown}
              placeholder="All Campaigns"
              loadingMessage="Loading campaigns"
              isLoading={loading}
              selected={get(filters, 'campaign', '')}
              options={
                filters.vertical
                  ? campaigns
                      .filter(pt => pt.verticalId === filters.vertical)
                      .map(pt => ({
                        value: pt.id,
                        text: decode(pt.name),
                      }))
                  : campaigns.map(pt => ({
                      value: pt.id,
                      text: decode(pt.name),
                    }))
              }
              onChange={e => setFilter('campaign', e.currentTarget.value)}
            />
            <GenericInput
              className={styles.dropdown}
              placeholder="SubTracking"
              value={get(filters, 'subTracking', '')}
              onChange={e => setFilter('subTracking', e.currentTarget.value)}
            />
          </div>
        </FilterBox>
      </div>
    </div>
  )
}

export default TableFilters
